'use client';

/**
 * ShortsShelf — the all-shorts RAG card ("Shorts Shelf" design).
 *
 * When every reference is a YouTube Short we drop the generic master-detail
 * (RelatedMediaPanel) for a purpose-built vertical layout: a channel-style
 * header carrying the one brand we own, then a horizontal snap-rail of 9:16
 * cards (or a centered row for 1-2 shorts). Tap a card == play it inline in
 * place via ShortsPlayer (our own controls); exactly one plays at a time.
 *
 * Per-card chrome is intentionally minimal — no our-title, no description. The
 * baked-in title lives in the thumbnail, and the header links out to YouTube
 * ("open on YT to read"). The account name isn't in our data (DocumentReference
 * has no channel field), so brand attribution appears ONCE, in the header.
 *
 * Mixed results never reach here — pick-media routes any non-short set to the
 * proven MediaHeroCard / RelatedMediaPanel (16:9 is never letterboxed to 9:16).
 */

import React, { memo, useRef, useState } from 'react';
import type { RAGReference } from '@/types/rag';
import { cn } from '@/lib/utils';
import { getYouTubeId, youTubeThumbnailUrl } from '@/lib/youtube';
import { useYouTubeDuration } from '@/hooks/useYouTubeDuration';
import { CountPill } from '@/components/assistant/primitives/count-pill';
import { defaultPreview } from './default-preview';
import { PlayButton, DurationBadge, YouTubeChip } from './media-hero-card';
import { ShortsPlayer } from './shorts-player';

export const ShortsShelf = memo(function ShortsShelf({
  references,
}: {
  references: RAGReference[];
}) {
  // One card plays at a time — switching reverts the previous card to its poster
  // (mirrors RelatedMediaPanel's panel-level `playing`). Hooks before any return.
  const [playingIdx, setPlayingIdx] = useState<number | null>(null);
  if (references.length === 0) return null;

  // n>=3 → scroll rail; n<=2 → centered larger cards so a lone/paired short
  // reads as intentional, not a stub rail.
  const isRail = references.length >= 3;
  const widthClass = isRail ? 'w-[148px]' : 'w-[172px]';

  const cards = references.map((r, i) => (
    <ShortsCard
      key={`${r.url}-${i}`}
      refItem={r}
      widthClass={widthClass}
      playing={playingIdx === i}
      onPlay={() => setPlayingIdx(i)}
    />
  ));

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-[14px] rounded-[20px] border-[1px] border-[#f0e7da] bg-white px-[20px] py-[18px]',
        'shadow-[0_1px_4px_rgba(0,0,0,0.04)]',
        "font-['Work_Sans']"
      )}
    >
      {/* ── Channel-style header: the ONLY place brand/account appears; the
          link doubles as the "open on YouTube to read" escape hatch. ── */}
      <div className="flex items-center justify-between gap-2">
        <a
          href={references[0].url}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex min-w-0 items-center gap-[10px] no-underline"
        >
          <YouTubeChip size={26} />
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-[14px] font-semibold leading-tight text-[#31364e] underline-offset-2 group-hover:underline">
              Art of Living
            </span>
            <span className="text-[11.5px] font-bold uppercase tracking-[0.09em] text-[#8a7c68]">
              YouTube · Shorts
            </span>
          </span>
        </a>
        <CountPill
          count={references.length}
          noun={references.length === 1 ? 'Short' : 'Shorts'}
        />
      </div>

      {/* ── Body: scroll rail (n>=3) or centered row (n<=2) ── */}
      {isRail ? (
        <div className="relative -mx-[20px]">
          <div className="flex snap-x snap-mandatory gap-[12px] overflow-x-auto overscroll-x-contain scroll-px-[20px] px-[20px] pb-[2px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {cards}
          </div>
          {/* Right-edge fade = the "there's more, scroll →" affordance. */}
          <span className="pointer-events-none absolute inset-y-0 right-0 w-[32px] bg-gradient-to-l from-white to-transparent" />
        </div>
      ) : (
        <div className="flex justify-center gap-[14px]">{cards}</div>
      )}
    </div>
  );
});

// ── One 9:16 short card: idle poster ⇄ inline ShortsPlayer ──

function ShortsCard({
  refItem,
  widthClass,
  playing,
  onPlay,
}: {
  refItem: RAGReference;
  widthClass: string;
  playing: boolean;
  onPlay: () => void;
}): React.ReactElement {
  const youtubeId = getYouTubeId(refItem.url);
  const duration = useYouTubeDuration(refItem.url);
  const btnRef = useRef<HTMLButtonElement | null>(null);

  const boxClass = cn(
    'relative aspect-[9/16] shrink-0 snap-start overflow-hidden rounded-[14px] border-[1px] border-[#e6ddd0] bg-black',
    widthClass
  );

  if (playing && youtubeId) {
    return (
      <div className={boxClass}>
        <ShortsPlayer videoId={youtubeId} title={refItem.title} />
      </div>
    );
  }

  const handlePlay = () => {
    onPlay();
    // Bring the active card fully into view before it starts (rail case).
    btnRef.current?.scrollIntoView({
      behavior: 'smooth',
      inline: 'start',
      block: 'nearest',
    });
  };

  return (
    <button
      ref={btnRef}
      type="button"
      onClick={handlePlay}
      aria-label={refItem.title ? `Play ${refItem.title}` : 'Play short'}
      className={cn(boxClass, 'group cursor-pointer p-0')}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={
          youtubeId
            ? youTubeThumbnailUrl(youtubeId)
            : defaultPreview(refItem).src
        }
        alt=""
        // hqdefault is 16:9 → object-cover fits the height in a 9:16 box;
        // bias the crop upward so Gurudev's face / baked-in title survives.
        className="absolute inset-0 size-full object-cover object-[center_35%]"
        onError={(e) => {
          const img = e.currentTarget;
          if (img.dataset.fallback) return;
          img.dataset.fallback = '1';
          img.src = defaultPreview(refItem).src;
        }}
      />
      <span className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(0,0,0,0)_58%,rgba(0,0,0,0.34)_100%)]" />
      <PlayButton size={40} />
      {/* "Short Video" replaces the old "Video" label; top-left clears the
          baked-in title, which sits center/lower. Idle state only. */}
      <span className="absolute left-[9px] top-[9px] rounded-[6px] bg-black/55 px-[7px] py-[2px] text-[10.5px] font-semibold uppercase tracking-[0.06em] text-white backdrop-blur-[2px]">
        Short Video
      </span>
      {duration && (
        <DurationBadge duration={duration} position="bottom-right" />
      )}
    </button>
  );
}

export default ShortsShelf;
